import { useCallback, useEffect } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { isNil } from 'ramda';
import { useField } from 'react-final-form';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import {
  IKnmErknmFormViolation,
  IKnmErknmFormViolationFix,
  IViolationEliminationFormAttachment,
  IViolationMaterialTypeItem,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IViolationMaterialTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IViolationMaterialTypeSelectOption';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { ViolationFixMaterialTypesEnum } from 'APIPackage/KnmModule/enums/ViolationFixMaterialTypesEnum';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';

import styles from './ErknmDecisionViolationFixFactTypeField.module.scss';

interface IErknmDecisionViolationFixFactTypeFieldProps {
  violation: IKnmErknmFormViolation;
  fieldName: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  fix: IKnmErknmFormViolationFix;
  inAct?: boolean;
  isDisabledByDirty?: boolean;
  isDisabledByAgreement?: boolean;
  hasUnsignedAct?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  id: string;
}

export const ErknmDecisionViolationFixFactTypeField = ({
  fieldName,
  formRef,
  violation,
  fix,
  inAct,
  isDisabledByDirty = false,
  isDisabledByAgreement = false,
  hasUnsignedAct = false,
  isKnm,
  isPm,
  isDecision,
  id,
}: IErknmDecisionViolationFixFactTypeFieldProps) => {
  const typesFieldName = `${fieldName}.type`;
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const handleAdd = useCallback(() => {
    const newMethodItem: IViolationMaterialTypeItem = {
      isNew: true,
      value: null,
    };

    push && push(typesFieldName, newMethodItem);
  }, []);
  const handleRemove = useCallback(
    (idx: number) => () => {
      remove && remove(typesFieldName, idx);
    },
    [],
  );

  const typesValidation = useCallback(
    (value: IViolationMaterialTypeItem[]) => {
      if ((fix.isNew || hasUnsignedAct) && (!value || value.length === 0)) {
        return REQUIRED_ERROR;
      }
    },
    [fix.isNew, hasUnsignedAct],
  );

  const transformSelectOptions = useCallback(
    (arr: IViolationMaterialTypeSelectOption[] | undefined) =>
      arr?.filter(option => !fix.type?.map(item => item.value?.value).includes(option.value)),
    [fix],
  );

  const {
    input: { value: typeChange },
    meta: { dirty: isTypeDirty, initial },
  } = useField(typesFieldName, { subscription: { value: true, dirty: true, initial: true } });

  useEffect(() => {
    if (isTypeDirty && initial) {
      // @ts-ignore
      const currentAttachments: IViolationEliminationFormAttachment[] = formRef?.current.getFieldState(
        `${fieldName}.attachments` as keyof IKnmForm,
      )?.value;

      const newAttachments: IViolationEliminationFormAttachment[] = [];
      let hasExplanationMaterial: boolean = false;

      if (
        Array.isArray(typeChange) &&
        typeChange.length > 0 &&
        Array.isArray(currentAttachments) &&
        currentAttachments.length > 0
      ) {
        typeChange.forEach(item => {
          if (item?.value?.code === ViolationFixMaterialTypesEnum.DOCUMENT) {
            const documentAttachment = currentAttachments?.find(itm =>
              itm.attachment?.mimeType?.includes('application'),
            );

            if (documentAttachment) {
              newAttachments.push(documentAttachment);
            }
          }

          if (item?.value?.code === ViolationFixMaterialTypesEnum.VIDEO) {
            const videoLinkAttachment = currentAttachments?.find(itm => !!itm.link);

            if (videoLinkAttachment) {
              newAttachments.push(videoLinkAttachment);
            }
          }

          if (item?.value?.code === ViolationFixMaterialTypesEnum.PHOTO) {
            const imageAttachment = currentAttachments?.find(itm => itm.attachment?.mimeType?.includes('image'));

            if (imageAttachment) {
              newAttachments.push(imageAttachment);
            }
          }

          if (item?.value?.code === ViolationFixMaterialTypesEnum.EXPLANATION) {
            hasExplanationMaterial = true;
          }
        });
      }

      // @ts-ignore
      formRef?.current.change(
        `${fieldName}.attachments` as keyof IKnmForm,
        newAttachments.length > 0 ? newAttachments : undefined,
      );

      // @ts-ignore
      !hasExplanationMaterial && formRef?.current.change(`${fieldName}.explanationMaterial` as keyof IKnmForm);
      !hasExplanationMaterial &&
        // @ts-ignore
        formRef?.current.change(`${fieldName}.explanationMaterialReactQuillEditor` as keyof IKnmForm);
    }
  }, [typeChange]);

  return (
    <>
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Тип материала',
          field: typesFieldName,
          isPm,
          isKnm,
          isDecision,
        })}
        id={`${id}.type`}
        labelFontSize="small"
        color="black"
        button={
          fix.isNew || hasUnsignedAct ? (
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              onClick={handleAdd}
              disabled={isDisabledByDirty || isDisabledByAgreement}
            />
          ) : undefined
        }
      />
      <FieldArray name={typesFieldName} validate={typesValidation}>
        {({ fields, meta }) => {
          return (
            <>
              {fields.map((typeFieldName, idx) => {
                const typeValue = fields.value[idx];

                const selectOptions = (violation.fixPlanMethods
                  ?.map(item => item.value)
                  .filter(
                    method => !isNil(method) && !fields.value?.some(item => item.value?.value === method?.value),
                  ) || []) as ISelectOption[];

                return (
                  <Row key={typeFieldName} notSpace>
                    <FieldBlock
                      className={typeValue.isNew ? styles.FullWidthWithButton : undefined}
                      id={`${id}.type[${idx}].value`}
                    >
                      {inAct ? (
                        <SelectInputFieldAutoOptions
                          id={`${typeFieldName}.value`}
                          name={`${typeFieldName}.value`}
                          optionName={CommonCatalogsNamesEnum.VIOLATION_MATERIAL_TYPE}
                          isClearable={false}
                          placeholder="Выберите способ"
                          validate={makeRequired('Поле не может быть пустым')}
                          disabled={!(typeValue.isNew || hasUnsignedAct) || isDisabledByDirty || isDisabledByAgreement}
                          transformFunc={transformSelectOptions}
                        />
                      ) : (
                        <SelectInputField
                          name={`${typeFieldName}.value`}
                          options={selectOptions}
                          isClearable={false}
                          placeholder="Выберите тип материала"
                          disabled={!(typeValue.isNew || hasUnsignedAct) || isDisabledByDirty || isDisabledByAgreement}
                          validate={makeRequired('Поле не может быть пустым')}
                        />
                      )}
                    </FieldBlock>
                    {typeValue.isNew && !isDisabledByDirty && !isDisabledByAgreement && (
                      <CloseButton className="shared-deleting-row-btn" onClick={handleRemove(idx)} />
                    )}
                  </Row>
                );
              })}
              {typeof meta.error === 'string' && meta.touched && (
                <p className="shared-input-error shared-row__small-margin">{meta.error}</p>
              )}
            </>
          );
        }}
      </FieldArray>
    </>
  );
};
